import React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import produce, { setAutoFreeze } from "immer";
import { FormHelperText, makeStyles } from "@material-ui/core";
import { toast } from "react-toastify";
import _ from "lodash";
import { Cancel } from "@material-ui/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ScreeningSections from "../ScreeningSections";
import { useLoading } from "../../../../util/LoadingProvider";
import editorConfiguration from "../../../../util/EditorConfiguration";
import apiHelper from "../../../../util/ApiHelper";
import { modal } from "../../../../util";

const frontendUrl = process.env.REACT_APP_CINEFAN_WEB_HOSTNAME;
setAutoFreeze(false);

const defaultFormValues = {
  localizations: {
    en: {
      slug: "",
      title: "",
      description: "",
      orgTitle: "",
      director: "",
      cast: "",
      year: "",
      country: "",
      runtime: "",
      fileFormat: "",
      filmStyle: "",
      note: "",
      filmWebPage: "",
      remark: "",
      language: "",
    },
    tc: {
      slug: "",
      title: "",
      description: "",
      orgTitle: "",
      director: "",
      cast: "",
      year: "",
      country: null,
      runtime: "",
      fileFormat: "",
      filmStyle: "",
      note: "",
      filmWebPage: "",
      remark: "",
      language: "",
    },
  },
  slides: [],
  tags: [],
  screening: [],
  publishDate: null,
  status: null,
};

const schema = yup.object().shape({
  localizations: yup.object().shape({
    en: yup.object().shape({
      slug: yup.string().required("Slug is required."),
      title: yup.string().required("English movie name is required."),
    }),
    tc: yup.object().shape({
      slug: yup.string().required("Slug is required."),
      title: yup.string().required("Chinese movie name is required."),
    }),
  }),
  screening: yup.array().of(
    yup.object().shape({
      url: yup
        .string()
        .nullable()
        .optional()
        .matches(RegExp(/http:\/\/|https:\/\//g), {
          message: "Missing http:// or https://",
          excludeEmptyString: true,
        }),
      startDate: yup.string().nullable().required("Start date required."),
      startTime: yup.string().nullable().required("Start time required."),
      endDateTime: yup
        .string()
        .test("endDateTime", "Please choose a end date.", (value, context) => {
          switch (context.parent.type) {
            case "offline":
              return true;
            case "online":
              return !(!context.parent.endDate || !context.parent.endTime);

            default:
              return true;
          }
        }),
      remarks: yup
        .array()
        .min(1, "Need to select at least one venue.")
        .required("Need to select at least one venue."),
    })
  ),
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const useStyles = makeStyles((theme) => ({
  tabpanel: {
    background: "#FFFFFF",
  },
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  imageGallery: {
    border: "1px solid #CCCCCC",
    minHeight: "160px",
    borderRadius: "3px",
    position: "relative",
  },
  imageGalleryControl: {
    position: "relative",
    width: "100%",
    display: "flex",
    height: "40px",
    bottom: "0",
    borderTop: "1px solid #CCCCCC",
    padding: "5px",
  },
  imageGalleryBlk: {
    minHeight: "120px",
    padding: "5px",
  },
  imgPreview: {
    maxWidth: "200px",
    margin: "10px",
    display: "inline-flex",
    position: "relative",
    verticalAlign: "top",
    "& img": {
      width: "100%",
      height: "auto",
    },
    "& button": {
      position: "absolute",
      top: "-10px",
      right: "-10px",
      zIndex: "10",
      display: "block",
      color: "rgba(48, 111, 157, 0.8)",
      background: "white",
      padding: 0,
      transition: "200ms all ease-out",
      "&:hover": {
        color: "rgba(48, 111, 157, 1)",
        background: "white",
      },
    },
  },
  permalink: {
    paddingLeft: "5px",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    "& .permalink": {
      fontWeight: 700,
    },
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

// function LinkTab(props) {
//   return (
//     <Tab
//       onClick={(event) => {
//         event.preventDefault();
//       }}
//       {...props}
//     />
//   );
// }

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

export default function EditMoviePage({ disabled, ...props }) {
  const { match, history } = props;
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState(0);
  const [defaultValues, setDefaultValues] = React.useState(defaultFormValues);
  const [remarkList, setRemarkList] = React.useState([]);
  const [tagList, setTagList] = React.useState([]);
  const { setLoading } = useLoading();
  const [formError, setFormError] = React.useState({});
  // keep in case
  // const [clonedEnPermalink, setClonedEnPermalink] = React.useState("");
  // const [clonedTcPermalink, setClonedTcPermalink] = React.useState("");
  // const [isEnSlugInvalid, setIsEnSlugInvalid] = React.useState(false);
  // const [isTcSlugInvalid, setIsTcSlugInvalid] = React.useState(false);
  // const [isEditPermalink, setEditPermalink] = React.useState({
  //   en: false,
  //   tc: false,
  // });

  const [clonedPermalink, setClonedPermalink] = React.useState("");
  const [isEditPermalink, setEditPermalink] = React.useState(false);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    reset,
    setValue,
    formState,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const status = watch("status");

  const getAllRemarks = async () => {
    try {
      setLoading(true);
      const result = await apiHelper.cinefan.getAllRemarks();
      setRemarkList(result.data.content);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllTags = async () => {
    try {
      setLoading(true);
      const result = await apiHelper.cinefan.getAllTags();
      setTagList(result.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getMovieDetail = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.cinefan.getMovieById(match.params.id);
      result.data.tags = result.data.tags.map((o) => o.id);
      result.data.screening = result.data.screening.map(
        ({ startTime, endTime, ...rest }) => {
          console.log({ startTime, endTime });
          return {
            ...rest,
            startTime: startTime
              ? startTime?.split(":")?.slice(0, 2)?.join(":")
              : startTime,
            endTime: endTime
              ? endTime?.split(":")?.slice(0, 2)?.join(":")
              : endTime,
          };
        }
      );
      console.log(result.data);
      setDefaultValues(result.data);
      setClonedPermalink(result.data.localizations.en.slug);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [match.params.id]);

  const uploadFile = async (file) => {
    try {
      setLoading(true);
      const result = await apiHelper.cinefan.uploadFile(file);
      console.log(result.data.url);
      return result.data.url;
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    console.log(event);
    for (var i = 0, files = event.target.files; i < files.length; i++) {
      const file = event.target.files[i];
      if (file) {
        const url = await uploadFile(file);
        const slidesValue = getValues("slides");
        const newSlidesValue = [...slidesValue, url];
        setValue(`slides`, newSlidesValue, { shouldDirty: true });
      }
    }
  };

  const removeImgFromSlides = (url) => {
    const slidesValue = getValues("slides");

    const newSlidesValue = slidesValue.filter((item) => item !== url);

    setValue(`slides`, newSlidesValue, { shouldDirty: true });
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onSubmit = (articleStatus) => async (value) => {
    setLoading(true);
    let data = prepareScreening(value);
    data = produce(data, (draft) => {
      draft.status = articleStatus;
      if (data.screening && data.screening.length > 0) {
        data.screening = data.screening.map(({ id, ...rest }) => rest);
      }
      return draft;
    });

    console.log("data", data);
    if (!data.localizations.en.slug || !data.localizations.tc.slug) {
      toast.error("Slug required.");
      setLoading(false);
      return;
    }
    try {
      if(match.params.id) {
        //update
        const result = await apiHelper.cinefan.updateMovieById(
        match.params.id,
        data
        );
        console.log(result);
        if (result.data.id && articleStatus === "PREVIEW") {
          window.open(
            frontendUrl + "/preview/movies/" + result.data.id,
            "_blank"
          );
        }
        if (result.status === 200) {
          getMovieDetail();
          toast.dark("Your changes is saved.");
        }
      }else {
        // create 
        const result = await apiHelper.cinefan.createMovie(data);
        console.log(result);
        if (result.data.id && articleStatus === "PREVIEW") {
          window.open(
            frontendUrl + "/preview/movies/" + result.data.id,
            "_blank"
          );
        } else if (result.data.id) {
          toast.dark("Movie is added.");
          history.push(`/cinefan/movies/edit/${result.data.id}`);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const prepareScreening = (value) => {
    const newScreeningList = produce(value, (draft) => {
      if (draft.screening) {
        draft.screening = produce(draft.screening, (screeningListDraft) => {
          return screeningListDraft.map((o) => {
            const screeningDraft = Object.assign({}, o);
            if (screeningDraft.remarks) {
              screeningDraft.remarks = screeningDraft.remarks.map(
                (remark) => remark.id
              );
              screeningDraft.startTime = screeningDraft.startTime
                ? `${screeningDraft.startTime}:00`
                : screeningDraft.startTime;
              screeningDraft.endTime = screeningDraft.endTime
                ? `${screeningDraft.endTime}:00`
                : screeningDraft.endTime;
              if (screeningDraft.type === "offline") {
                screeningDraft.endTime = null;
                screeningDraft.endDate = null;
              }
            }
            return screeningDraft;
          });
        });
      }
      // draft.tags = draft.tags.map((o) => o.id);
      return draft;
    });
    return newScreeningList;
  };

  const deleteMovie = async (id) => {
    const decision = await modal.fire({
      title: "Delete Movie",
      text: "Are you sure to delete this movie?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.cinefan.deleteMovie(id);
        console.log(result);
      } catch (err) {
        console.log(err);
      }
    }
  };

  const { fields, append, remove } = useFieldArray(
    {
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "screening", // unique name for your Field Array
      // keyName: "id", default to "id", you can change the key name
    }
  );

  React.useEffect(() => {
    getAllRemarks();
    getAllTags();
  }, []);

  React.useEffect(() => {
    if(match.params.id) {
      getMovieDetail();  
    }
  }, [getMovieDetail]);

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  React.useEffect(() => {
    if (!_.isEqual(formError, formState.errors)) {
      setFormError(formState.errors);
      if (formState.errors?.localizations?.en) {
        setActiveTab(0);
      }
      if (formState.errors?.localizations?.tc) {
        setActiveTab(1);
      }
    }
  }, [formState, formError]);

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        Movies > Edit Movie
      </Typography>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
      >
        <Tab
          label="English"
          {...a11yProps(0)}
        />
        <Tab
          label="繁體中文"
          {...a11yProps(1)}
        />
      </Tabs>
      <form>
        <fieldset disabled={disabled} className="fieldset">
          <TabPanel value={activeTab} index={0}>
            <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name={`localizations.en.title`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Movie Name"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        defaultValue={field.value}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.permalink}>
                    <span className="permalink">Permalink: </span>
                    {!isEditPermalink && (
                      <>
                        {frontendUrl || "https://cinefan.com.hk"}/movie/
                        {getValues(`localizations.en.slug`)
                          ? getValues(`localizations.en.slug`)
                          : defaultValues &&
                            defaultValues.localizations.en.slug}
                        <Button
                          variant="outlined"
                          disabled={disabled}
                          onClick={() => {
                            setEditPermalink(true);
                            setClonedPermalink(
                              getValues(`localizations.en.slug`)
                                ? getValues(`localizations.en.slug`)
                                : defaultValues &&
                                    defaultValues.localizations.en.slug
                            );
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {isEditPermalink && (
                      <>
                        <span>
                          {frontendUrl || "https://cinefan.com.hk"}/movie/
                        </span>
                        <Controller
                          name={`localizations.en.slug`}
                          control={control}
                          defaultValue={
                            defaultValues && defaultValues.localizations.en.slug
                          }
                          render={({
                            field: { value, onChange, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              {...rest}
                              value={value}
                              onChange={(event) => {
                                onChange(event);
                                setValue(
                                  "localizations.tc.slug",
                                  event.target.value
                                );
                              }}
                              error={!!error}
                              helperText={error ? "slug required" : ""}
                            />
                          )}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (getValues(`localizations.en.slug`)) {
                              setEditPermalink(false);
                            }
                          }}
                        >
                          OK
                        </Button>
                        <Button
                          variant="text"
                          onClick={() => {
                            setValue(`localizations.en.slug`, clonedPermalink);
                            setEditPermalink(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        disabled={disabled}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.orgTitle"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Original Title"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.director"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Director"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.cast"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Cast"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.year"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Year"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.country"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Country"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.runtime"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Runtime"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.fileFormat"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="File Format"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.filmStyle"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Film Style"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : "The way a movie is filmed, as in the techniques that are used in the production process"
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.filmWebPage"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Film Web Page"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : "Insert the full URL / Link (e.g.: https://en.wikipedia.org/wiki/To_Catch_a_Thief)"
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.remark"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Additional Info"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.en.language"
                      control={control}
                      defaultValue={defaultFormValues.localizations.en.language}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Language"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name={`localizations.tc.title`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Movie Name"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.permalink}>
                    <span className="permalink">Permalink: </span>
                    {!isEditPermalink && (
                      <>
                        {frontendUrl || "https://cinefan.com.hk"}/zh/movie/
                        {getValues(`localizations.tc.slug`)
                          ? getValues(`localizations.tc.slug`)
                          : defaultValues &&
                            defaultValues.localizations.tc.slug}
                        <Button
                          variant="outlined"
                          disabled={disabled}
                          onClick={() => {
                            setEditPermalink(true);
                            setClonedPermalink(
                              getValues(`localizations.tc.slug`)
                                ? getValues(`localizations.tc.slug`)
                                : defaultValues &&
                                    defaultValues.localizations.tc.slug
                            );
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {isEditPermalink && (
                      <>
                        <span>
                          {frontendUrl || "https://cinefan.com.hk"}/zh/movie/
                        </span>
                        <Controller
                          name={`localizations.tc.slug`}
                          control={control}
                          defaultValue={defaultFormValues.localizations.tc.slug}
                          render={({
                            field: { value, onChange, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              variant="outlined"
                              margin="dense"
                              {...rest}
                              value={value}
                              onChange={(event) => {
                                onChange(event);
                                setValue(
                                  "localizations.en.slug",
                                  event.target.value
                                );
                              }}
                              error={!!error}
                              helperText={error ? "slug required" : ""}
                            />
                          )}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (getValues(`localizations.tc.slug`)) {
                              setEditPermalink(false);
                            }
                          }}
                        >
                          OK
                        </Button>
                        <Button
                          variant="text"
                          onClick={() => {
                            setValue(`localizations.tc.slug`, clonedPermalink);
                            setEditPermalink(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        disabled={disabled}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.orgTitle"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Original Title"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.director"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Director"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.cast"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Cast"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.year"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Year"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.country"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Country"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.runtime"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Runtime"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.fileFormat"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="File Format"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.filmStyle"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Film Style"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : "The way a movie is filmed, as in the techniques that are used in the production process"
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.filmWebPage"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Film Web Page"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : "Insert the full URL / Link (e.g.: https://en.wikipedia.org/wiki/To_Catch_a_Thief)"
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.remark"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Additional Info"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="localizations.tc.language"
                      control={control}
                      defaultValue={defaultFormValues.localizations.tc.language}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          label="Language"
                          variant="outlined"
                          margin="dense"
                          className={classes.textInput}
                          fullWidth
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
            <Grid>
              <Controller
                name="localizations.en.note"
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          type="checkbox"
                          color="primary"
                          checked={value}
                          {...rest}
                          onChange={(event) => {
                            console.log(event.target.checked);
                            setValue(
                              "localizations.en.note",
                              event.target.checked
                            );
                            setValue(
                              "localizations.tc.note",
                              event.target.checked
                            );
                          }}
                        />
                      }
                      label="This movie contains a note section"
                    />
                  );
                }}
              />
            </Grid>
            <Grid>
              <Controller
                name="localizations.en.subtitle"
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          type="checkbox"
                          color="primary"
                          checked={!!value}
                          {...rest}
                          onChange={(event) => {
                            console.log(event.target.checked);
                            setValue(
                              "localizations.en.subtitle",
                              event.target.checked
                            );
                            setValue(
                              "localizations.tc.subtitle",
                              event.target.checked
                            );
                          }}
                        />
                      }
                      label="This movie is screening with Chinese subtitle"
                    />
                  );
                }}
              />
              <FormHelperText>
                The result of this checkbox will appear only in the chinese
                translation of this movie with this wording: "中文字幕”
              </FormHelperText>
            </Grid>
          </Box>
          {remarkList.length > 0 && (
            <>
              <TabPanel value={activeTab} index={0}>
                <Box className={classes.innerBox}>
                  <Grid>
                    <Typography variant="span">Screening</Typography>
                    <ScreeningSections
                      disabled={disabled}
                      setValue={setValue}
                      control={control}
                      remarkList={remarkList}
                      lang={"en"}
                      fields={fields}
                      remove={remove}
                      append={append}
                    />
                  </Grid>
                </Box>
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <Box className={classes.innerBox}>
                  <Grid>
                    <Typography variant="span">Screening</Typography>
                    <ScreeningSections
                      disabled={disabled}
                      setValue={setValue}
                      control={control}
                      remarkList={remarkList}
                      lang={"tc"}
                      fields={fields}
                      remove={remove}
                      append={append}
                    />
                  </Grid>
                </Box>
              </TabPanel>
            </>
          )}

          <Box className={classes.innerBox}>
            <Grid container spacing={3} direction="column">
              <Grid container item xs={12} direction="column">
                <Typography variant="span">Film Images</Typography>
                <Controller
                  name="slides"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Grid item xs={12}>
                        <div className={classes.imageGallery}>
                          <div className={classes.imageGalleryBlk}>
                            {field.value.map((slide) => {
                              return (
                                <div className={classes.imgPreview} key={slide}>
                                  <img src={slide} alt={slide} />
                                  {disabled || (
                                    <IconButton
                                      type="button"
                                      onClick={() => removeImgFromSlides(slide)}
                                    >
                                      <Cancel />
                                    </IconButton>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {disabled || (
                            <div className={classes.imageGalleryControl}>
                              <input
                                style={{ display: "none" }}
                                id="upload-image"
                                type="file"
                                onChange={handleFileUpload}
                                multiple
                              />
                              <Button
                                component="label"
                                htmlFor="upload-image"
                                variant="contained"
                                disableElevation
                                style={{
                                  color: "white",
                                  background: "#306F9D",
                                  margin: "0px",
                                  padding: "9px 16px",
                                }}
                              >
                                Add to Gallery
                              </Button>
                            </div>
                          )}
                        </div>
                      </Grid>
                    );
                  }}
                />
              </Grid>
              <Grid container item xs={12} direction="column">
                <Typography variant="span">Movie Tag</Typography>
                <Grid item xs={6}>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        className={classes.formControl}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                      >
                        <Select
                          multiple
                          disabled={disabled}
                          {...field}
                          renderValue={(selected) => (
                            <div className={classes.chips}>
                              {tagList &&
                                tagList
                                  .filter((o) => selected.includes(o.id))
                                  .map((value) => (
                                    <Chip
                                      key={value.id}
                                      label={value.title}
                                      className={classes.chip}
                                    />
                                  ))}
                            </div>
                          )}
                          MenuProps={MenuProps}
                        >
                          {tagList &&
                            tagList.map((tag) => (
                              <MenuItem key={tag.id} value={tag.id}>
                                {tag.title}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.innerBox}>
            <Grid container xs={12} direction="column" spacing={3}>
              <Grid container item xs={12}>
                <Typography variant="span">Publish Settings</Typography>
              </Grid>
              <Grid container item xs={12}>
                <Grid container item xs={8} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name={`publishDate`}
                      control={control}
                      defaultValue={defaultFormValues.publishDate}
                      render={({
                        field: { value, onChange, ...rest },
                        fieldState: { error },
                      }) => (
                        <KeyboardDateTimePicker
                          fullWidth
                          minutesStep={1}
                          disabled={disabled}
                          variant="inline"
                          format="yyyy-MM-dd HH:mm:ss"
                          margin="dense"
                          id="date-picker-inline"
                          label="Date time"
                          {...rest}
                          value={value}
                          onChange={(date, value) => {
                            onChange(value);
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          KeyboardButtonProps={{
                            "aria-label": "change date",
                          }}
                          helperText={
                            "Your post will be posted immediately if you don’t set a custom date and time."
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="button"
                style={{ marginRight: "0px", padding: "9px 16px" }}
                onClick={handleSubmit(onSubmit("PREVIEW"))}
              >
                Preview
              </Button>
              <Button
                type="button"
                variant="outlined"
                disableElevation
                style={{
                  borderColor: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
                onClick={handleSubmit(onSubmit("DRAFT"))}
              >
                Save Draft
              </Button>
              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
                onClick={handleSubmit(onSubmit("PUBLISHED"))}
              >
                {status === "DRAFT" ? "Publish" : "Save"}
              </Button>
              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#962013",
                  padding: "9px 16px",
                }}
                onClick={() => deleteMovie(match.params.id)}
              >
                Delete
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
